import { isNil } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@shared/components/Icon';
import { AiSummaryButton, AiSummaryResult } from '@shared/scenes/ask/components/AiSummary';
import { useAiSummariesEnabled } from '@shared/scenes/ask/useAiSummariesEnabled';
import { isAiSummaryButtonShown, useAiSummary } from '@shared/scenes/ask/useAiSummary';
import { getComponentLabelWithFallback, isSubformComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { FIELD_SEARCH, useFeatureFlag } from '@shared/util/featureFlags';
import { Input } from '@ui/Input';
import { whiteInputStyles } from '@ui/Input/styles';
import { buildCardSummaryPrompt } from './buildCardSummaryPrompt';
import { useCardSummary } from './useCardSummary';
import styles from './FormToolbar.module.css';

const searchInputWrapper = `${whiteInputStyles.inputWrapper} h-10 min-h-10 shadow-none`;

interface Props {
  query: string;
  onQueryChange: (query: string) => void;
  /** The card (workflow-entity) id — the Ask scope the summary is resolved against. */
  entityId?: string;
  /** The card's own workflow template id, so the prompt can distinguish it from its subforms. */
  workflowTemplateId: string;
  /** All components on the card; subform components are surfaced to the prompt by name and template. */
  components: API.AnyComponent[];
}

/**
 * Tools for the form as a whole, above its fields and divided from them: the field search, and
 * the "Summarize" button once the card exists. The summary it generates shows below the row.
 */
export function FormToolbar({
  query,
  onQueryChange,
  entityId,
  workflowTemplateId,
  components,
}: Props) {
  const { t } = useTranslation();
  const isSearchEnabled = useFeatureFlag(FIELD_SEARCH);
  const isSummaryEnabled = useAiSummariesEnabled();
  const cardSummary = useCardSummary();

  const buildPrompt = useCallback(() => {
    const subforms = components.filter(isSubformComponent).map(component => ({
      name: getComponentLabelWithFallback(component),
      workflowTemplateIds: component.workflowTemplateIds ?? [],
    }));
    return buildCardSummaryPrompt({ workflowTemplateId, subforms });
  }, [workflowTemplateId, components]);

  const onSummaryGenerated = useCallback(
    (summary: string) => cardSummary?.setSummary(summary),
    [cardSummary],
  );
  const { status, summarize } = useAiSummary({
    scope: { type: 'card', id: entityId ?? '' },
    buildPrompt,
    onSummaryGenerated,
  });

  // The summary needs a saved card and a card-summary provider (i.e. the editable card view).
  const summary = isSummaryEnabled && !isNil(cardSummary) && !isNil(entityId) ? cardSummary : null;

  const isSummaryButtonShown = !isNil(summary) && isAiSummaryButtonShown(summary.summary, status);

  if (!isSearchEnabled && isNil(summary)) {
    return null;
  }

  return (
    <div className={styles.toolbar}>
      {(isSearchEnabled || isSummaryButtonShown) && (
        <div className={styles.toolbarRow}>
          {isSearchEnabled && (
            <div className={styles.toolbarSearch}>
              <Input
                value={query}
                onValueChange={onQueryChange}
                placeholder={t('b.viewTemplates.settings.searchComponents')}
                size="sm"
                rounded="full"
                isClearable
                classNames={{ inputWrapper: searchInputWrapper }}
                startContent={
                  <Icon name="magnifying-search-01" className="text-gray-30" gridSize={3} />
                }
              />
            </div>
          )}
          {isSummaryButtonShown && (
            <AiSummaryButton onClick={summarize} testIDPrefix="card" size="medium" />
          )}
        </div>
      )}
      {!isNil(summary) && <AiSummaryResult summary={summary.summary} status={status} />}
    </div>
  );
}
